import { useState, useEffect, useRef, Dispatch, useCallback } from 'react';
import { ChatAudio } from '@/customTypes/chatStory';

const usePlayAudio = (
  initialPlayState: 'on' | 'off' = 'on',
): [Dispatch<ChatAudio | undefined>, () => void, () => void, 'on' | 'off'] => {
  const [playState, setPlayState] = useState<'on' | 'off'>('on');
  const [audioInfo, _setAudioInfo] = useState<ChatAudio>();
  const audioInfoRef = useRef<ChatAudio | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const isSafari = useRef<boolean>(false);

  /**
   * React 생명 주기와 무관한 외부 이벤트 리스너에서 발생하는 클로저 문제 해결을 위해 ref 와 같이 관리
   *
   * 이전 이슈 : domEventListener 에서 playAudio 호출 시 초기 audioInfo(undefined)를 참조하던 이슈
   */
  const setAudioInfo = useCallback(
    (audioInfoChanging: ChatAudio | undefined) => {
      _setAudioInfo(audioInfoChanging);
      audioInfoRef.current = audioInfoChanging;
    },
    [],
  );

  useEffect(() => {
    const agent = navigator.userAgent.toLowerCase();
    const isSafariAgent = !agent.includes('chrome') && agent.includes('safari');

    // 사파리 브라우저이거나 초기 playState가 off인 경우
    if (isSafariAgent || initialPlayState === 'off') {
      isSafari.current = true;
      audioRef.current.muted = true;
      setPlayState('off');
      return;
    }

    isSafari.current = false;
    audioRef.current.muted = false;
    setPlayState('on');

    return () => {
      isSafari.current && (audioRef.current.muted = true);
      audioRef.current.pause();
      audioRef.current.src = '';
    };
  }, []);

  useEffect(() => {
    audioInfo?.type !== 'BGM' &&
      audioRef.current?.addEventListener('ended', clearSoundEffect);
    return () => {
      audioInfo?.type !== 'BGM' &&
        audioRef.current?.removeEventListener('ended', clearSoundEffect);
    };
  }, [audioInfo]);

  const clearSoundEffect = () => {
    audioInfo?.type !== 'BGM' && setAudioInfo(undefined);
  };

  const playAudio = () => {
    setPlayState('on');
    isSafari.current && (audioRef.current.muted = false);
    if (audioInfoRef.current) {
      if (audioRef.current.src !== audioInfoRef.current.audioLink) {
        audioRef.current.src = audioInfoRef.current.audioLink;
      }
      audioRef.current.loop = audioInfoRef.current.type === 'BGM';
      audioRef.current.play();
    }
  };

  const pauseAudio = () => {
    setPlayState('off');
    isSafari.current && (audioRef.current.muted = true);
    audioRef.current.pause();
  };

  const cleanupAudio = () => {
    audioRef.current.pause();
  };

  useEffect(() => {
    if (audioInfo) {
      playState === 'on' && playAudio();
      clearSoundEffect();
    }
  }, [audioInfo]);

  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, []);

  return [setAudioInfo, playAudio, pauseAudio, playState];
};

export default usePlayAudio;
